import { useFastCheckoutContext } from "@/components/Checkout/CheckoutMachine/FastCheckoutMachineProvider";

import { MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import { alpha, Box, styled } from "@mui/material";
import { useSelector } from "@xstate/react";
import ProgressTracker from "../components/ProgressAnimation";
import { getCheckoutHeaderRenderConfig } from "@/components/Checkout/utils";
import { ArrowRightIcon } from "@brandclub/common-ui";

import SaveButton from "../components/Buttons/SaveButton";
import { ErrorDot } from "@/components/StoreComponents/StoreError";

import { FastCheckoutState } from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import StateBadge from "../components/StateBadge";
import { UserRetailerCheckoutOrderStatusV2 } from "@/components/Checkout/types";
import { Link } from "react-router-dom";
import { useDashboardLinkPrefix } from "@/utils/hooks/useDashboardLinkPrefix";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",

  ".header_label_receipt": {
    display: "flex",
    alignItems: "center",
    gap: 24,
    color: theme.palette.primary.main,
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "10px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: "22px",
      marginBottom: "6px",
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: "22px",
      marginBottom: "6px",
      gap: 12,
    },
  },

  ".header_label": {
    display: "flex",
    alignItems: "center",
    gap: 24,
    color: theme.palette.primary.main,
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "10px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: "22px",
      marginBottom: "6px",
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: "22px",
      marginBottom: "6px",
      gap: 12,
    },
  },
  ".header_sub_label": {
    color: theme.palette.primary.main,
    fontSize: "18px",
    fontWeight: 500,
    marginBottom: "20px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: "16px",
      marginBottom: "24px",
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {},
  },
}));

const OrderNotificationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: "24px",
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: "32px",
  },
  ".order_notification": {
    marginTop: "25px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "20px",
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      marginTop: "16px",
      fontSize: "14px",
    },
  },
}));

const OrderNotification = ({
  headerRenderConfig,
}: {
  headerRenderConfig: {
    headerText: string;
    headerLabel: string;
    actionButton?: string;
    error: boolean;
  };
}) => {
  const { actorRef } = useFastCheckoutContext();

  const handleRetryCheckout = () => {
    actorRef.send({
      type: "ON_RETRY_CHECKOUT",
    });
  };

  return (
    <OrderNotificationContainer>
      <div className="order_notification">
        <div className="text">{headerRenderConfig.headerText}</div>
        {headerRenderConfig.error && (
          <ErrorDot>
            <div style={{ color: "#fff", fontSize: "10px" }}>1</div>
          </ErrorDot>
        )}
      </div>
      {headerRenderConfig.actionButton === "retry_checkout" && (
        <SaveButton
          sx={{
            paddingLeft: "100px",
            paddingRight: "100px",
            fontSize: "16px",
            maxWidth: "unset",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              paddingLeft: "20px",
              paddingRight: "20px",
              fontSize: "14px",
            },
          }}
          onClick={handleRetryCheckout}
        >
          Retry Checkout
        </SaveButton>
      )}
    </OrderNotificationContainer>
  );
};

const RewardContainer = styled("div")(({ theme }) => ({
  padding: "25px 0",
  display: "flex",

  width: "100%",
  boxSizing: "border-box",
  flexDirection: "row",
  justifyContent: "space-between",
  backgroundColor: alpha(theme.palette.secondary.main, 0.2),
  borderRadius: 8,
  alignItems: "center",
  paddingLeft: 25,
  paddingRight: 25,
  paddingVertical: 18,
  gap: 26,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "18px 0",
    paddingLeft: 25,
    paddingRight: 25,
    gap: 16,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: "18px 0",
    paddingLeft: 15,
    paddingRight: 15,
    gap: 16,
  },
  ".content": {
    gap: 8,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 4,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 4,
    },
  },
  ".title": {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.primary,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".text": {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.primary,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
  ".icon": {
    width: 70,
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    borderRadius: 50,

    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      width: 50,
      height: 50,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 50,
      height: 50,
    },
    ".svg": {
      width: 42,
      height: 42,
      fill: theme.palette.primary.main,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        width: 32,
        height: 32,
      },
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        width: 32,
        height: 32,
      },
    },
  },
  ".button_icon": {
    width: 26,
    height: 26,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      width: 18,
      height: 18,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 18,
      height: 18,
    },
  },
}));

const SuccessReward = ({ title, text }: { title: string; text: string }) => {
  const prefix = useDashboardLinkPrefix();
  return (
    <RewardContainer>
      <div className="icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32.385 32.385"
          className="svg"
        >
          <path
            data-name="Path 25766"
            d="M15.179 1.9a4.258 4.258 0 0 1 4.528 0l3.025 1.9a1.966 1.966 0 0 0 .618.256l3.483.8a4.26 4.26 0 0 1 3.2 3.2l.8 3.483a1.966 1.966 0 0 0 .256.618l1.9 3.025a4.257 4.257 0 0 1 0 4.528l-1.9 3.025a1.966 1.966 0 0 0-.256.618l-.8 3.483a4.26 4.26 0 0 1-3.2 3.2l-3.483.8a1.966 1.966 0 0 0-.618.256l-3.025 1.9a4.257 4.257 0 0 1-4.528 0l-3.025-1.9a1.966 1.966 0 0 0-.618-.256l-3.483-.8a4.26 4.26 0 0 1-3.2-3.2l-.8-3.483a1.966 1.966 0 0 0-.256-.618L1.9 19.707a4.258 4.258 0 0 1 0-4.528l1.9-3.025a1.966 1.966 0 0 0 .256-.618l.8-3.483a4.26 4.26 0 0 1 3.2-3.2l3.483-.8a1.966 1.966 0 0 0 .618-.256l3.025-1.9zm2.264 4.62a10.921 10.921 0 1 0 10.921 10.923A10.927 10.927 0 0 0 17.443 6.522zm0 2.259a8.661 8.661 0 1 1-8.661 8.661 8.664 8.664 0 0 1 8.661-8.659zm0 12.069 2.071 1.274a1.37 1.37 0 0 0 2.062-1.436l-.5-2.481 1.609-1.61a1.37 1.37 0 0 0-.871-2.336l-2.162-.155-.952-2.223a1.37 1.37 0 0 0-2.519 0l-.955 2.225-2.162.155A1.37 1.37 0 0 0 12.2 16.6l1.609 1.61-.5 2.481a1.37 1.37 0 0 0 2.062 1.436z"
            fillRule="evenodd"
            transform="translate(-1.25 -1.25)"
          />
        </svg>
      </div>
      <div className="content" style={{ flex: 1 }}>
        <div className="title">{title}</div>
        <div className="text">{text}</div>
      </div>
      <Link to={prefix + "/rewards/balance"}>
        <ArrowRightIcon
          fontSize="large"
          className="button_icon"
          color="primary"
        />
      </Link>
    </RewardContainer>
  );
};

const ProcessingHead = () => {
  const { actorRef } = useFastCheckoutContext();

  const order = useSelector(
    actorRef,
    (snapshot) => snapshot.context?.retailerCheckoutOrderStatus
  );

  const headerRenderConfig = getCheckoutHeaderRenderConfig(
    order?.displayStatusInfo?.stage,
    order?.userRetailerCheckoutOrder
  );

  const activeStage = useSelector(
    actorRef,
    (snapshot) => snapshot.context.activeStage
  );
  const errorStage = useSelector(
    actorRef,
    (snapshot) => snapshot.context.error?.stage
  );

  if (!order) {
    return null;
  }

  return (
    <Container className="section">
      <div className="header_label">{headerRenderConfig.headerLabel}</div>
      <div className="header_sub_label">
        Order #{order?.userRetailerCheckoutOrder?.orderId}
      </div>
      <ProgressTracker activeStep={activeStage} errorStep={errorStage} />
      <OrderNotification headerRenderConfig={headerRenderConfig} />
    </Container>
  );
};

const ReceiptHead = () => {
  const { actorRef } = useFastCheckoutContext();
  const response = useSelector(
    actorRef,
    (snapshot) => snapshot.context?.retailerCheckoutOrderStatus
  );

  const order = response?.userRetailerCheckoutOrder;

  // TODO: get reward amount
  const rewardAmount = 0;

  const headerRenderConfig = getCheckoutHeaderRenderConfig(
    response?.displayStatusInfo?.stage,
    response?.userRetailerCheckoutOrder,
    order?.status === UserRetailerCheckoutOrderStatusV2.checkout_error
  );

  const activeStage = useSelector(
    actorRef,
    (snapshot) => snapshot.context.activeStage
  );

  return (
    <Container className="section">
      <div className="header_label_receipt">
        Order details
        <StateBadge type={order?.status as UserRetailerCheckoutOrderStatusV2} />
      </div>
      <div className="header_sub_label">
        Order #{response?.userRetailerCheckoutOrder?.orderId}
      </div>
      {order?.status === UserRetailerCheckoutOrderStatusV2.checkout_error ? (
        <>
          <ProgressTracker
            activeStep={activeStage}
            errorStep={activeStage}
            animate={false}
          />
          <OrderNotification headerRenderConfig={headerRenderConfig} />
        </>
      ) : order?.status ===
          UserRetailerCheckoutOrderStatusV2.checkout_success &&
        rewardAmount > 0 ? (
        <SuccessReward
          title="Congratulations!"
          text={`You earned ${rewardAmount} in new rewards`}
        />
      ) : null}
    </Container>
  );
};

const CheckoutHead = () => {
  const { actorRef } = useFastCheckoutContext();
  const checkoutState = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );

  if (checkoutState === FastCheckoutState.RECEIPT_GENERATED) {
    return <ReceiptHead />;
  } else {
    return <ProcessingHead />;
  }
};

export default CheckoutHead;
